import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { getMasterData } from '@/libs/fleet-api/services/master-data';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	DatePicker,
	Flex,
	Form,
	FormInstance,
	Input,
	Row,
	Select,
} from 'antd';
import React, { useState } from 'react';
import formRule from '@/libs/antd/rules';
import { FleetQueryParams } from '@/libs/fleet-api/fetcher';
import { getPipelineList } from '@/libs/fleet-api/services/pipeline';
import { useForm } from '@/libs/antd/form';

type PipelineListSearchProps = {
	refetch: () => void;
	searchForm: {
		form: FormInstance<{
			by: 'period' | 'customer-name' | 'status';
			value: string;
		}>;
		value: Partial<{
			by: 'period' | 'customer-name' | 'status';
			value: string;
		}> | null;
		fieldName: (name: 'value' | 'by' | ['value'] | ['by']) => unknown;
	};
};

const PipelineListSearch: React.FC<PipelineListSearchProps> = ({
	refetch,
	searchForm,
}) => {
	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');

	const updateDateValue = (newStartDate: string, newEndDate: string) => {
		const formattedValue = `${newStartDate}|${newEndDate}`;
		searchForm.form.setFieldsValue({ ['value']: formattedValue });
	};

	const { data: prospectGroupStatus } = useQuery({
		queryKey: ['getMasterData'],
		queryFn: () => getMasterData('prospect-group-status'),
	});

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			layout="horizontal"
			colon={false}
			labelAlign="left"
			onFinish={() => refetch()}
			form={searchForm.form}
		>
			<Row gutter={[20, 20]}>
				<Col flex="auto">
					<Form.Item
						label="Search By"
						labelCol={{ flex: 'auto' }}
						wrapperCol={{ style: { width: 250 } }}
						name={searchForm.fieldName('by')}
						rules={[formRule.required()]}
					>
						<Select
							allowClear
							options={[
								{ label: 'Periode', value: 'period' },
								{ label: 'Nama Customer', value: 'customer-name' },
								{ label: 'Status Pengajuan', value: 'status' },
							]}
							onChange={(value) => {
								searchForm.form.setFieldsValue({ ['value']: '' });
								searchForm.form.setFieldsValue({ ['by']: value });
							}}
							onClear={() => {
								searchForm.form.resetFields();
								refetch();
							}}
							placeholder="Search By"
							style={{ display: 'block', width: '100%' }}
						/>
					</Form.Item>
				</Col>
				<Col flex="auto">
					{searchForm.value?.by === 'period' ? (
						<Flex gap={12}>
							<Form.Item
								name={searchForm.fieldName('value')}
								hidden
								noStyle
								rules={[formRule.required()]}
							/>
							<Form.Item
								name="startDate"
								rules={[formRule.required()]}
								style={{ flex: 1 }}
							>
								<DatePicker
									onChange={(date) => {
										const newStartDate = date?.format('YYYY-MM-DD') || '';
										setStartDate(newStartDate);
										updateDateValue(newStartDate, endDate);
									}}
								/>
							</Form.Item>
							<Form.Item
								name="endDate"
								rules={[formRule.required()]}
								style={{ flex: 1 }}
							>
								<DatePicker
									onChange={(date) => {
										const newEndDate = date?.format('YYYY-MM-DD') || '';
										setEndDate(newEndDate);
										updateDateValue(startDate, newEndDate);
									}}
								/>
							</Form.Item>
						</Flex>
					) : searchForm.value?.by === 'status' ? (
						<Form.Item
							name={searchForm.fieldName('value')}
							rules={[formRule.required()]}
						>
							<Select options={prospectGroupStatus?.data} />
						</Form.Item>
					) : (
						<Form.Item
							name={searchForm.fieldName('value')}
							rules={[formRule.required()]}
						>
							<Input
								placeholder="Search..."
								style={{ display: 'block' }}
							/>
						</Form.Item>
					)}
				</Col>
				<Col>
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
						>
							SEARCH
						</Button>
					</Form.Item>
				</Col>
			</Row>
		</Form>
	);
};

const usePipelineListSearch = ({
	plafonType,
}: {
	plafonType: 'NonJoinFinancing' | 'JoinFinancing';
}) => {
	const searchForm = useForm<{
		by: 'period' | 'customer-name' | 'status';
		value: string;
	}>();

	const { data, isFetching, refetch, isError } = useQuery({
		queryKey: ['getPipelineList'],
		queryFn: () => {
			const params: FleetQueryParams = {
				pagination: { pageSize: 20, pageNumber: 1 },
				filter: {
					by: 'plafon-type',
					value: plafonType,
				},
				...(searchForm.value?.by || searchForm.value?.value
					? {
							search: {
								by: searchForm.value.by ?? '',
								value: searchForm.value.value ?? '',
							},
						}
					: {}),
			};
			return getPipelineList(params);
		},
		enabled: true,
	});

	return {
		data,
		isFetching,
		isError,
		PipelineListSearch: () => (
			<PipelineListSearch
				refetch={refetch}
				searchForm={searchForm}
			/>
		),
	};
};

export default usePipelineListSearch;
